import { createFileRoute, notFound } from '@tanstack/react-router'

import { NotFoundPage } from '@/components/not-found'
import { type PostCardData } from '@/features/blog/components/post-card'
import { ArticlePage, type Post } from '@/features/blog/components/article-page'
import { pageHead, resolveOgImage, SITE_URL } from '@/lib/seo'
import {
  articleSchema,
  breadcrumbSchema,
  faqSchema,
  homeBreadcrumbItem,
} from '@/lib/structured-data'
import { sanityClient } from '@/sanity/client'
import { urlFor } from '@/sanity/image'
import { postQuery, relatedPostsQuery } from '@/sanity/queries'

export const Route = createFileRoute('/$locale/blog/$slug')({
  loader: async ({ params }) => {
    const [post, related] = await Promise.all([
      sanityClient.fetch<Post | null>(postQuery(params.locale), {
        slug: params.slug,
      }),
      sanityClient.fetch<PostCardData[]>(relatedPostsQuery(params.locale), {
        slug: params.slug,
      }),
    ])
    if (!post) throw notFound()
    return { post, related }
  },
  // Placed here (not on `/$locale`) so the leaf match is the notFound
  // boundary: only this match's own `component` gets bypassed, while
  // `/$locale`'s LocaleLayout (Header/Footer) stays resolved as an ancestor
  // and renders this in its Outlet.
  notFoundComponent: () => <NotFoundPage />,
  head: ({ loaderData, params }) => {
    const post = loaderData?.post
    if (!post) return {}
    const ownPath = `/blog/${params.slug}`
    const otherPath = post.otherSlug ? `/blog/${post.otherSlug}` : undefined
    const altPaths =
      params.locale === 'en'
        ? { fr: otherPath, en: ownPath }
        : { fr: ownPath, en: otherPath }
    const title = post.seoTitle ?? post.title
    const description = post.seoDescription ?? post.excerpt ?? ''
    const ogImage = post.ogImage
      ? urlFor(post.ogImage).width(1200).height(630).fit('crop').url()
      : undefined
    const image = resolveOgImage(ogImage, title)
    const url = `${SITE_URL}/${params.locale}${ownPath}`
    const articleLd = articleSchema({
      title: post.title,
      description,
      datePublished: post.publishedAt,
      dateModified: post._updatedAt,
      authorName: post.author?.name,
      url,
      image,
    })
    const breadcrumbLd = breadcrumbSchema([
      homeBreadcrumbItem(params.locale),
      { name: 'Blog', url: `${SITE_URL}/${params.locale}/blog` },
      { name: post.title, url },
    ])
    const faqLd = faqSchema(post.faq)
    return pageHead({
      locale: params.locale,
      path: ownPath,
      title,
      description,
      ogImage,
      altPaths,
      jsonLd: faqLd
        ? [articleLd, faqLd, breadcrumbLd]
        : [articleLd, breadcrumbLd],
    })
  },
  component: BlogPost,
})

function BlogPost() {
  const { post, related } = Route.useLoaderData()
  const { locale, slug } = Route.useParams()
  return (
    <ArticlePage post={post} related={related} locale={locale} slug={slug} />
  )
}
